import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from './components/LanguageSwitcher';
import traceMopIcon from './tracemop-icon.png';

const TEAL = '#14b8a6';
const TEAL_DIM = '#0F766E';
const TEAL_GLOW = 'rgba(20,184,166,0.12)';
const TEAL_BORDER = 'rgba(20,184,166,0.22)';
const BG = '#0a0e17';
const BG_CARD = '#111827';
const WHITE = '#f8fafc';
const MUTED = '#94a3b8';

const styles = {
  page: {
    position: 'relative',
    width: '100vw',
    minHeight: '100vh',
    background: BG,
    fontFamily: "'Inter', system-ui, sans-serif",
    WebkitFontSmoothing: 'antialiased',
    overflow: 'hidden',
  },
  grid: {
    display: 'none',
  },
  nav: {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 32px',
    height: 56,
    borderBottom: `1px solid rgba(255,255,255,0.05)`,
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoName: {
    fontSize: 17,
    fontWeight: 800,
    letterSpacing: '-0.3px',
    color: WHITE,
  },
  logoAccent: {
    color: TEAL,
  },
  loginBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'transparent',
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 6,
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: TEAL,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s, border-color 0.15s',
  },
  stage: {
    position: 'relative',
    zIndex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px 60px',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 999,
    padding: '5px 16px',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: TEAL,
    marginBottom: 28,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: TEAL,
    animation: 'pulse 2s infinite',
  },
  headline: {
    fontSize: 'clamp(40px, 7vw, 72px)',
    fontWeight: 800,
    letterSpacing: '-2.5px',
    lineHeight: 1.0,
    color: WHITE,
    marginBottom: 20,
  },
  headlineAccent: {
    color: TEAL,
  },
  sub: {
    fontSize: 16,
    color: MUTED,
    maxWidth: 400,
    lineHeight: 1.75,
    marginBottom: 44,
  },
  divider: {
    width: 48,
    height: 2,
    background: TEAL,
    margin: '0 auto 36px',
  },
  emailRow: {
    display: 'flex',
    width: '100%',
    maxWidth: 400,
    marginBottom: 16,
  },
  emailInput: {
    flex: 1,
    background: BG_CARD,
    border: `1px solid ${TEAL_BORDER}`,
    borderRight: 'none',
    borderRadius: '6px 0 0 6px',
    padding: '12px 16px',
    fontSize: 14,
    fontFamily: 'inherit',
    color: WHITE,
    outline: 'none',
  },
  emailBtn: {
    background: TEAL_DIM,
    border: 'none',
    borderRadius: '0 6px 6px 0',
    padding: '12px 20px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: '#fff',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  orRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 400,
    marginBottom: 16,
  },
  orLine: {
    flex: 1,
    height: 1,
    background: 'rgba(255,255,255,0.07)',
  },
  orText: {
    fontSize: 11,
    color: MUTED,
    fontWeight: 500,
    letterSpacing: '1px',
  },
  googleBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    maxWidth: 400,
    background: TEAL_GLOW,
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 6,
    padding: '11px 20px',
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: WHITE,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  wave: {
    marginTop: 52,
    opacity: 0.18,
  },
  footer: {
    position: 'fixed',
    bottom: 0, left: 0, right: 0,
    padding: '12px 32px',
    borderTop: `1px solid rgba(255,255,255,0.04)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 11,
    color: MUTED,
    zIndex: 10,
  },
  // already-logged-in state
  loggedInBox: {
    background: BG_CARD,
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 10,
    padding: '28px 32px',
    textAlign: 'center',
    maxWidth: 360,
    width: '100%',
  },
};

function TraceMopLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <img
        src={traceMopIcon}
        alt=""
        style={{
          height: 72,
          width: 72,
          filter: 'invert(1) hue-rotate(180deg) saturate(1.4)',
        }}
      />
      <span style={{ fontSize: 17, fontWeight: 700, color: WHITE, letterSpacing: '-0.2px' }}>
        Trace Mop
      </span>
    </div>
  );
}

const LoginPage = ({ setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [cropZoom, setCropZoom] = useState(1);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    return () => {
      if (profilePhotoPreview) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
    };
  }, [profilePhotoPreview]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err === 'email_login_required') {
      setLoginError('Ez a fiok email + jelszo bejelentkezest hasznal.');
    } else if (err === 'unauthorized') {
      setLoginError('Nincs jogosultsag ehhez a fiokhoz.');
    } else if (err === 'auth_failed') {
      setLoginError('Google bejelentkezes sikertelen.');
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch('/auth/userinfo', { credentials: 'include' });
        const data = await response.json();
        if (data.success) {
          setUserInfo(data.user);
          setUser(data.user);
          localStorage.setItem('user', JSON.stringify(data.user));
          setProfileName(data.user?.name || '');
        }
      } catch (err) {
        // not logged in
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [setUser]);

  const handleLogin = () => {
    window.location.href = '/auth/login/google';
  };

  const handleConfirm = () => {
    navigate('/');
  };

  const handleSwitchAccount = async () => {
    await fetch('/auth/logout', { credentials: 'include' });
    localStorage.removeItem('user');
    setUser(null);
    setUserInfo(null);
    setLoginEmail('');
    setLoginPassword('');
    setLoginError('');
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (!loginEmail.trim() || !loginPassword) {
      setLoginError('Add meg az emailt es a jelszot.');
      return;
    }
    setLoginLoading(true);
    try {
      const response = await fetch('/auth/login/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setLoginError(data.detail || 'Hibas email vagy jelszo.');
        return;
      }
      if (data.success && data.user) {
        setUserInfo(data.user);
        setUser(data.user);
        setShowLoginModal(false);
        localStorage.setItem('user', JSON.stringify(data.user));
        if (data.user.type === 'cleaner') {
          navigate('/app');
        } else {
          navigate('/');
        }
      }
    } catch (err) {
      console.error('Email login hiba:', err);
      setLoginError('Bejelentkezesi hiba, probald ujra.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileName.trim() || !profilePhoto || !profilePhotoPreview) {
      alert('A nev es a foto kotelezo.');
      return;
    }

    const sourceImg = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = profilePhotoPreview;
    });

    const cropSize = 256;
    const canvas = document.createElement('canvas');
    canvas.width = cropSize;
    canvas.height = cropSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      alert('Nem sikerult a kep feldolgozasa.');
      return;
    }

    const baseScale = Math.max(cropSize / sourceImg.width, cropSize / sourceImg.height);
    const renderedWidth = sourceImg.width * baseScale * cropZoom;
    const renderedHeight = sourceImg.height * baseScale * cropZoom;
    const drawX = (cropSize - renderedWidth) / 2 + cropX;
    const drawY = (cropSize - renderedHeight) / 2 + cropY;
    ctx.drawImage(sourceImg, drawX, drawY, renderedWidth, renderedHeight);

    const croppedBlob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
    if (!croppedBlob) {
      alert('Nem sikerult a kep crop mentese.');
      return;
    }

    const formData = new FormData();
    formData.append('name', profileName.trim());
    formData.append('photo', croppedBlob, 'profile.jpg');

    setSavingProfile(true);
    try {
      const response = await fetch('/auth/complete-profile', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.detail || 'Nem sikerult menteni a profilt.');
        return;
      }
      if (data.success) {
        setUserInfo(data.user);
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (err) {
      console.error('Profile complete hiba:', err);
      alert('Hiba a profil mentes soran.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoSelect = (file) => {
    if (!file) {
      setProfilePhoto(null);
      setProfilePhotoPreview('');
      return;
    }

    if (profilePhotoPreview) {
      URL.revokeObjectURL(profilePhotoPreview);
    }

    const previewUrl = URL.createObjectURL(file);
    setProfilePhoto(file);
    setProfilePhotoPreview(previewUrl);
    setCropZoom(1);
    setCropX(0);
    setCropY(0);
  };

  if (loading) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: MUTED, fontSize: 13 }}>{t('login.loading')}</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        input::placeholder { color: #475569; }
        input:focus { border-color: ${TEAL} !important; }
        .login-btn:hover { background: ${TEAL_GLOW} !important; border-color: ${TEAL} !important; }
        .google-btn:hover { background: rgba(20,184,166,0.18) !important; }
        .email-btn:hover { background: ${TEAL} !important; }
      `}</style>

      <div style={styles.grid} />

      {/* Nav */}
      <nav style={styles.nav}>
        <TraceMopLogo />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <LanguageSwitcher compact />
          <button
            className="login-btn"
            style={styles.loginBtn}
            onClick={userInfo ? handleConfirm : () => setShowLoginModal(true)}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="5" r="2.5" stroke={TEAL} strokeWidth="1.4"/>
              <path d="M2 12c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5" stroke={TEAL} strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            {userInfo ? t('login.openApp') : t('login.login')}
          </button>
        </div>
      </nav>

      {/* Login modal */}
      {showLoginModal && !userInfo && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowLoginModal(false); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div style={{
            background: BG_CARD,
            border: `1px solid ${TEAL_BORDER}`,
            borderRadius: 14,
            padding: '28px 24px',
            width: '100%',
            maxWidth: 380,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: WHITE }}>{t('login.loginTitle')}</span>
              <button
                onClick={() => setShowLoginModal(false)}
                style={{ background: 'none', border: 'none', color: MUTED, fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
              >×</button>
            </div>
            <form onSubmit={handleEmailPasswordLogin} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="email@tracemop.com"
                autoComplete="username"
                style={{ ...styles.emailInput, borderRadius: 6, borderRight: `1px solid ${TEAL_BORDER}` }}
              />
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder={t('login.password')}
                autoComplete="current-password"
                style={{ ...styles.emailInput, borderRadius: 6, borderRight: `1px solid ${TEAL_BORDER}` }}
              />
              {loginError && (
                <div style={{ fontSize: 12, color: '#f87171' }}>{loginError}</div>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                className="email-btn"
                style={{ ...styles.emailBtn, borderRadius: 6, width: '100%' }}
              >
                {loginLoading ? t('login.loginSubmitting') : t('login.loginSubmit')}
              </button>
            </form>
            <div style={styles.orRow}>
              <div style={styles.orLine} />
              <span style={styles.orText}>{t('login.or')}</span>
              <div style={styles.orLine} />
            </div>
            <button type="button" className="google-btn" style={styles.googleBtn} onClick={handleLogin}>
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
              </svg>
              {t('login.googleLogin')}
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={styles.stage}>
        {userInfo ? (
          <div style={styles.loggedInBox}>
            {!userInfo.profile_completed ? (
              <form onSubmit={handleProfileSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>{t('login.firstLogin')}</div>
                <div style={{ fontSize: 12, color: WHITE, marginBottom: 4 }}>{userInfo.email}</div>
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder={t('login.fullName')}
                  style={{ ...styles.emailInput, borderRadius: 6, borderRight: `1px solid ${TEAL_BORDER}` }}
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePhotoSelect(e.target.files?.[0] || null)}
                  style={{ color: WHITE, fontSize: 12 }}
                />
                {profilePhotoPreview && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, color: MUTED, textAlign: 'left' }}>
                      {t('login.cropHint')}
                    </div>
                    <div
                      style={{
                        width: 180,
                        height: 180,
                        margin: '0 auto',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: `1px solid ${TEAL_BORDER}`,
                        position: 'relative',
                        background: '#0f172a',
                      }}
                    >
                      <img
                        src={profilePhotoPreview}
                        alt="Crop preview"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          width: 'auto',
                          height: 'auto',
                          minWidth: '100%',
                          minHeight: '100%',
                          transform: `translate(-50%, -50%) translate(${cropX}px, ${cropY}px) scale(${cropZoom})`,
                          transformOrigin: 'center center',
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                        draggable={false}
                      />
                    </div>

                    <label style={{ fontSize: 11, color: MUTED, textAlign: 'left' }}>
                      {t('login.zoom')}
                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.01"
                        value={cropZoom}
                        onChange={(e) => setCropZoom(Number(e.target.value))}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: MUTED, textAlign: 'left' }}>
                      {t('login.posX')}
                      <input
                        type="range"
                        min="-120"
                        max="120"
                        step="1"
                        value={cropX}
                        onChange={(e) => setCropX(Number(e.target.value))}
                        style={{ width: '100%' }}
                      />
                    </label>
                    <label style={{ fontSize: 11, color: MUTED, textAlign: 'left' }}>
                      {t('login.posY')}
                      <input
                        type="range"
                        min="-120"
                        max="120"
                        step="1"
                        value={cropY}
                        onChange={(e) => setCropY(Number(e.target.value))}
                        style={{ width: '100%' }}
                      />
                    </label>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={savingProfile}
                  style={{ ...styles.googleBtn, background: TEAL_DIM, border: 'none', color: '#fff' }}
                >
                  {savingProfile ? t('login.saving') : t('login.saveProfile')}
                </button>
                <button
                  type="button"
                  onClick={handleSwitchAccount}
                  style={{ ...styles.googleBtn, background: 'transparent', fontSize: 13, color: MUTED }}
                >
                  {t('login.switchAccount')}
                </button>
              </form>
            ) : (
              <>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 8 }}>{t('login.signedInAs')}</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: WHITE, marginBottom: 4 }}>{userInfo.email}</div>
                <div style={{ fontSize: 12, color: TEAL, marginBottom: 20 }}>{userInfo?.company?.name || '-'}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    onClick={handleConfirm}
                    style={{ ...styles.googleBtn, background: TEAL_DIM, border: 'none', color: '#fff' }}
                  >
                    {t('login.openTraceMop')}
                  </button>
                  <button
                    onClick={handleSwitchAccount}
                    style={{ ...styles.googleBtn, background: 'transparent', fontSize: 13, color: MUTED }}
                  >
                    {t('login.switchAccount')}
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={styles.badge}>
              <span style={styles.dot} />
              {t('login.comingSoon')}
            </div>

            <h1 style={styles.headline}>
              {t('login.heroLine1')}<br />
              {t('login.heroLine2before')}
              <span style={styles.headlineAccent}>{t('login.heroLine2accent')}</span>
              {t('login.heroLine2after')}
            </h1>

            <div style={styles.divider} />

            <p style={styles.sub}>
              {t('login.subtitle')}
            </p>

            <form
              onSubmit={(e) => e.preventDefault()}
              style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  style={{ ...styles.emailInput, flex: 1, borderRadius: 6, borderRight: `1px solid ${TEAL_BORDER}` }}
                />
                <button
                  type="submit"
                  className="email-btn"
                  style={{ ...styles.emailBtn, borderRadius: 6, whiteSpace: 'nowrap', padding: '0 18px' }}
                >
                  {t('login.notify')}
                </button>
              </div>
              <div style={{ fontSize: 11, color: MUTED, textAlign: 'left' }}>
                {t('login.notifyHint')}
              </div>
            </form>

          </>
        )}
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>{t('login.footerCopyright')}</span>
        <span>tracemop.com</span>
      </footer>
    </div>
  );
};

export default LoginPage;
